import { inject, injectable } from 'inversify'
import { SERVICE_TYPES } from '@/core/container/types'
import { BroadcastEvent, type IUserLoggedInEvent } from '@/services/transport/types'
import type WindowService from '@/services/browser/windowService'
import type { TmRoutesDictionary } from '@/services/route/routerTypes'
import type { ICurrentRouteService } from '@/services/route/types'
import type { IBroadcastSubscriptionService } from '@/services/types'

@injectable()
export default class LoggedInStatusService {
  private userLoggedIn = false

  private _isLogOutByUnauthorizedApiError = false

  constructor(
    @inject(SERVICE_TYPES.SubscriptionService) private readonly subscriptionService: IBroadcastSubscriptionService,
    @inject(SERVICE_TYPES.WindowService) private readonly windowService: WindowService,
    @inject(SERVICE_TYPES.RouterService) private readonly routerService: ICurrentRouteService,
  ) {
    this.subscriptionService.subscribeBroadcast(BroadcastEvent.UserLoggedIn, (payload) => {
      // there is no case when user on one tab is logged in and on the another one is logged out
      if (payload.isLogged && !this.userLoggedIn && this.isAllowRefreshConfirmRegistrationRoute(payload)) {
        this.windowService.getLocation().href = '/'
      }
    })
  }

  /**
   * it is necessary to keep the confirmation tab open when the user logs in under another account
   */
  public isAllowRefreshConfirmRegistrationRoute(payload: IUserLoggedInEvent) {
    const confirmRegistrationRouteName = 'auth.signup.verifyEmail' satisfies keyof TmRoutesDictionary
    const currentRoute = this.routerService.getCurrentRoute()
    if (currentRoute.name !== confirmRegistrationRouteName) {
      return true
    }
    const userIdRouteKey = 'userId' satisfies keyof TmRoutesDictionary[typeof confirmRegistrationRouteName]['query']
    const userId = currentRoute.query[userIdRouteKey]
    return payload.userId === userId
  }

  public setUserLoggedIn(userId: string) {
    this.userLoggedIn = true
    this._isLogOutByUnauthorizedApiError = false
    this.subscriptionService.broadcastEmit(BroadcastEvent.UserLoggedIn, { isLogged: true, userId })
  }

  public setUserLoggedOut(isLogOutByUnauthorizedApiError = false) {
    this.userLoggedIn = false
    this._isLogOutByUnauthorizedApiError = isLogOutByUnauthorizedApiError
    this.subscriptionService.broadcastEmit(BroadcastEvent.UserLoggedIn, { isLogged: false, userId: null })
  }

  public isUserLoggedIn() {
    return this.userLoggedIn
  }

  public isLogOutByUnauthorizedApiError() {
    return this._isLogOutByUnauthorizedApiError
  }
}
